// Wildcard / substring search helper for code-style identifiers.
//
// The token-prefix rule in token-prefix-search.ts deliberately rejects
// mid-string matches ("te" must not match "Wastewater"), which is right
// for human-readable names. Building names and FLOCs, however, are
// colon-separated codes like "BHM:BLD:Z1:C57" — colons are not token
// boundaries, so the whole code is a single token and field staff
// cannot find a building by the only fragment they know (the trailing
// "C57").
//
// This helper provides substring matching for those call sites and
// honours explicit "*" wildcards ("*C57", "BHM*C57"). Callers OR it
// with the token-prefix rule rather than replacing it, so every query
// that matched before still matches.

/**
 * Returns true when `query` matches `value` under wildcard-substring
 * semantics:
 *
 *   - "*" in the query matches any run of characters, including none.
 *   - A query without "*" matches anywhere in the value (plain
 *     case-insensitive substring), so "C57" matches "BHM:BLD:Z1:C57".
 *   - Multiple segments must appear in order without overlapping:
 *     "bld*c57" matches "BHM:BLD:Z1:C57", "c57*bld" does not.
 *   - Empty, whitespace-only, and all-"*" queries always return true
 *     so callers can treat them as a no-op, consistent with
 *     hasTokenPrefix.
 */
export function matchesWildcard(value: string, query: string): boolean {
  const normalisedQuery = query.trim().toLowerCase()
  if (normalisedQuery.length === 0) return true

  const segments = normalisedQuery.split('*').filter(nonEmpty)
  if (segments.length === 0) return true

  const loweredValue = value.toLowerCase()
  let searchFrom = 0
  for (const segment of segments) {
    const at = loweredValue.indexOf(segment, searchFrom)
    if (at === -1) return false
    searchFrom = at + segment.length
  }
  return true
}

function nonEmpty(s: string): boolean {
  return s.length > 0
}

/**
 * Multi-field convenience mirroring hasTokenPrefixOnAny: returns true
 * when `query` wildcard-matches ANY of the supplied values. Null and
 * undefined values are skipped (nullable PowerSync columns, optional
 * fields like FLOC).
 */
export function matchesWildcardOnAny(
  values: ReadonlyArray<string | null | undefined>,
  query: string,
): boolean {
  for (const value of values) {
    if (value !== null && value !== undefined && matchesWildcard(value, query)) {
      return true
    }
  }
  return false
}
